import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, ExternalLink } from 'lucide-react';
import GoogleAd from './ads/GoogleAd';

const Footer = () => {
  return (
    <footer className="bg-gray-900 text-white">
      <div className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="col-span-1 md:col-span-2">
            <div className="flex items-center space-x-3 mb-6">
              <img src="/assets/img/branding/logo.webp" alt="CarrierInfo Logo" className="w-10 h-10" width={40} height={40} loading="lazy" />
              <span className="text-2xl font-bold text-white">The Carrier Info</span>
            </div>
            <p className="text-gray-300 text-base mb-6 max-w-md leading-relaxed">
              Your comprehensive platform for trucking company information, carrier validation, and industry insights. 
              Connect with legitimate carriers and grow your logistics business.
            </p>
            <div className="flex space-x-4">
              <a href="https://instagram.com/thecarrierinfo" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors" aria-label="Instagram">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
              </a>
              <a href="https://wa.me/15302399204" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors" aria-label="WhatsApp">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-6 text-white">Quick Links</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/search" className="text-gray-300 hover:text-white transition-colors flex items-center group">
                  <span className="w-1 h-1 bg-amber-500 rounded-full mr-3 group-hover:bg-white transition-colors"></span>
                  Search Carriers
                </Link>
              </li>
              <li>
                <Link to="/validate" className="text-gray-300 hover:text-white transition-colors flex items-center group">
                  <span className="w-1 h-1 bg-amber-500 rounded-full mr-3 group-hover:bg-white transition-colors"></span>
                  Validate Phone/Email
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-gray-300 hover:text-white transition-colors flex items-center group">
                  <span className="w-1 h-1 bg-amber-500 rounded-full mr-3 group-hover:bg-white transition-colors"></span>
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/our-team" className="text-gray-300 hover:text-white transition-colors flex items-center group">
                  <span className="w-1 h-1 bg-amber-500 rounded-full mr-3 group-hover:bg-white transition-colors"></span>
                  Our Team
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-gray-300 hover:text-white transition-colors flex items-center group">
                  <span className="w-1 h-1 bg-amber-500 rounded-full mr-3 group-hover:bg-white transition-colors"></span>
                  Contact
                </Link>
              </li>
            </ul>
          </div>

          {/* Portal Links */}
          <div>
            <h3 className="text-lg font-semibold mb-6 text-white">Portal</h3>
            <ul className="space-y-3">
              <li>
                <a href="https://dial.thecarrierinfo.com/login" className="text-gray-300 hover:text-white transition-colors flex items-center group">
                  <span className="w-1 h-1 bg-amber-500 rounded-full mr-3 group-hover:bg-white transition-colors"></span>
                  <span>Login</span>
                  <ExternalLink className="w-3 h-3 ml-2 opacity-50" />
                </a>
              </li>
              <li>
                <a href="https://dial.thecarrierinfo.com/" className="text-gray-300 hover:text-white transition-colors flex items-center group">
                  <span className="w-1 h-1 bg-amber-500 rounded-full mr-3 group-hover:bg-white transition-colors"></span>
                  <span>Dashboard</span>
                  <ExternalLink className="w-3 h-3 ml-2 opacity-50" />
                </a>
              </li>
              <li>
                <a href="https://dial.thecarrierinfo.com/auth-register" className="text-gray-300 hover:text-white transition-colors flex items-center group">
                  <span className="w-1 h-1 bg-amber-500 rounded-full mr-3 group-hover:bg-white transition-colors"></span>
                  <span>Register</span>
                  <ExternalLink className="w-3 h-3 ml-2 opacity-50" />
                </a>
              </li>
              <li>
                <Link to="/privacy" className="text-gray-300 hover:text-white transition-colors flex items-center group">
                  <span className="w-1 h-1 bg-amber-500 rounded-full mr-3 group-hover:bg-white transition-colors"></span>
                  Privacy Policy
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Multiplex (Auto Relaxed) Ad */}
        <div className="my-8">
          <GoogleAd
            client="ca-pub-9619770339279979"
            slot="4241545647"
            format="autorelaxed"
          />
        </div>

        {/* Company Network Section */}
        <div className="border-t border-gray-800 mt-12 pt-8">
          <div className="bg-gradient-to-r from-gray-800 to-gray-850 rounded-lg p-6 mb-8">
            <h3 className="text-lg font-semibold mb-4 text-white flex items-center">
              <MapPin className="w-5 h-5 mr-2 text-amber-500" />
              Company Network & Locations
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* US Entity */}
              <div className="bg-gray-900 rounded-lg p-5 border border-gray-700 hover:border-amber-500 transition-colors">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 mt-1">
                    <div className="w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center">
                      <MapPin className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <span className="px-2 py-1 bg-blue-600 text-white text-xs font-semibold rounded">US Entity</span>
                      <span className="text-gray-400 text-xs">Operating Entity</span>
                    </div>
                    <h4 className="text-white font-bold text-lg mb-2">Denzik LLC</h4>
                    <div className="text-gray-300 text-sm space-y-1">
                      <p className="flex items-start">
                        <MapPin className="w-4 h-4 mr-2 mt-0.5 text-amber-500 flex-shrink-0" />
                        <span>117 South Lexington St, Ste 100<br/>Harrisonville, MO 64701</span>
                      </p>
                      <p className="text-xs text-gray-400 mt-2">🇺🇸 United States</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Parent/Network Entity */}
              <div className="bg-gray-900 rounded-lg p-5 border border-gray-700 hover:border-amber-500 transition-colors">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 mt-1">
                    <div className="w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                      </svg>
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <span className="px-2 py-1 bg-purple-600 text-white text-xs font-semibold rounded">Global Network</span>
                      <span className="text-gray-400 text-xs">Parent Company</span>
                    </div>
                    <h4 className="text-white font-bold text-lg mb-2">Denzik INT SMC PV LTD</h4>
                    <div className="text-gray-300 text-sm">
                      <p className="flex items-center mb-2">
                        <svg className="w-4 h-4 mr-2 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z"/>
                        </svg>
                        International Portfolio Network
                      </p>
                      <p className="text-xs text-gray-400">🌍 Global Operations</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-4 p-4 bg-gray-900 rounded-lg border border-gray-700">
              <p className="text-gray-300 text-sm">
                <strong className="text-white">About Our Network:</strong> The Carrier Info is operated by <strong className="text-amber-500">Denzik LLC</strong>, 
                a US-based entity, as part of the <strong className="text-purple-400">Denzik INT SMC PV LTD</strong> global portfolio. 
                We combine local expertise with international resources to deliver comprehensive trucking industry solutions.
              </p>
            </div>
          </div>
        </div>

        {/* Data Source & Disclaimer Section */}
        <div className="border-t border-gray-800 pt-8">
          <div className="bg-gray-800 rounded-lg p-6 mb-8">
            <h3 className="text-lg font-semibold mb-4 text-white">📋 Data Source & Disclaimer</h3>
            <div className="text-gray-300 text-sm space-y-2">
              <p>
                All carrier information displayed on The Carrier Info is sourced from publicly available datasets provided by:
              </p>
              <ul className="list-disc list-inside ml-4 space-y-1">
                <li><strong>U.S. Department of Transportation (USDOT)</strong> - Federal Motor Carrier Safety Administration (FMCSA)</li>
                <li><strong>FMCSA SAFER Database</strong> - Safety and Fitness Electronic Records System</li>
                <li><strong>FMCSA Snapshot Data</strong> - Public carrier records and safety information</li>
              </ul>
              <p className="mt-4">
                <strong>Disclaimer:</strong> The information provided is for informational purposes only and is obtained from public government records. 
                While we strive to maintain accuracy, we do not guarantee the completeness, accuracy, or timeliness of the information. 
                Users should verify critical information directly with the carrier or official government sources. 
                The Carrier Info is not affiliated with or endorsed by the U.S. Department of Transportation or FMCSA.
              </p>
              <p className="mt-2">
                <strong>Data Removal Requests:</strong> If you believe any information is incorrect or would like to request removal of your data, 
                please contact us at <a href="mailto:data-removal@thecarrierinfo.com" className="text-primary-400 hover:text-primary-300 underline">data-removal@thecarrierinfo.com</a> or visit our{' '}
                <Link to="/data-removal" className="text-primary-400 hover:text-primary-300 underline">Data Removal Request page</Link>.
              </p>
              <p className="mt-3 pt-3 border-t border-gray-700">
                <strong>Advertising Disclosure:</strong> This website uses Google AdSense and other third-party advertising networks to display advertisements. 
                These advertisers may use cookies and similar technologies to collect information about your browsing activities to provide personalized ads. 
                We may earn revenue from clicks or impressions on advertisements. For more information, see our{' '}
                <Link to="/privacy" className="text-primary-400 hover:text-primary-300 underline">Privacy Policy</Link>.
              </p>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-800 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex flex-col md:flex-row items-center space-y-2 md:space-y-0 md:space-x-6">
              <div>
                <p className="text-gray-400 text-sm mb-1">
                  © 2024 The Carrier Info. All rights reserved.
                </p>
                <p className="text-gray-300 text-sm">
                  Content © 2024 DENZIK INT (SMC-PVT) LTD. Carrier data is from public USDOT/FMCSA records.
                </p>
              </div>
              <div className="flex items-center flex-wrap gap-x-4 gap-y-2 text-sm text-gray-400">
                <Link to="/terms" className="hover:text-white transition-colors">Terms of Use</Link>
                <span className="hidden sm:inline">•</span>
                <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
                <span className="hidden sm:inline">•</span>
                <Link to="/help" className="hover:text-white transition-colors">Help Center</Link>
                <span className="hidden sm:inline">•</span>
                <Link to="/data-removal" className="hover:text-white transition-colors">Data Removal</Link>
                <span className="hidden sm:inline">•</span>
                <a href="mailto:privacy@thecarrierinfo.com?subject=Do%20Not%20Sell%20My%20Info" className="hover:text-white transition-colors">
                  Do Not Sell My Info
                </a>
              </div>
            </div>
            <div className="flex items-center space-x-4 mt-4 md:mt-0">
              <div className="flex items-center text-gray-400 text-sm">
                <Mail className="w-4 h-4 mr-2" />
                <a href="mailto:taha@thecarrierinfo.com" className="hover:text-white transition-colors">
                  taha@thecarrierinfo.com
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
